import * as ko from 'knockout';

import { app } from '../app';
import * as dashboardApi from '../api/dashboard';
import * as factsApi from '../api/facts';
import i18n from '../i18n';
import { session } from '../session';
import { Deferred } from '../utils/deferred';
import { confirmDialog } from './confirm_dialog';

const template = require('raw-loader!../../templates/components/observations_dialog.html').default;

// Identifies the clicked aggregate: the column's site (null = the across-sites column), the
// test-subject dimension ids it groups on, and — for the timeframe chart only — the visit date.
export interface ObservationsGroupKey {
  site_id: string | null;
  dimension_ids: string[];
  visit_date?: string;
  // Scatter only: each point is a single observation (one fact). fact_id is the definitive identity;
  // repetition_id is the fallback for derived values (no fact). Aggregating charts omit both and list
  // every contributing observation.
  repetition_id?: string | null;
  fact_id?: string | null;
}

export interface ObservationsDialogArgs {
  trialId: string;
  traitId: number;
  scheduledVisitId: number | null;
  visitGroupingType: string;
  siteIds: string[];
  alpha?: number;
  groupKey: ObservationsGroupKey;
  // Called after a value is deleted so the caller can reload the active analysis sub-tab.
  onChanged: () => void;
}

interface ObservationsDialogParams extends ObservationsDialogArgs {
  result: Deferred<void>;
}

class ObservationsDialog {
  private params: ObservationsDialogParams;

  rows = ko.observableArray<dashboardApi.GroupObservationRow>([]);
  loading = ko.observable(true);
  errorMessage = ko.observable('');
  canEdit = session.isAtLeastEditor();

  constructor(params: ObservationsDialogParams) {
    this.params = params;

    const query: dashboardApi.GroupObservationsParams = {
      trait_id: params.traitId,
      site_ids: params.siteIds,
      scheduled_visit_id: params.scheduledVisitId,
      visit_grouping_type: params.visitGroupingType,
      alpha: params.alpha,
      site_id: params.groupKey.site_id,
      dimension_ids: params.groupKey.dimension_ids,
      visit_date: params.groupKey.visit_date,
      repetition_id: params.groupKey.repetition_id ?? undefined,
      fact_id: params.groupKey.fact_id ?? undefined,
    };

    dashboardApi
      .fetchTraitGroupObservations(params.trialId, query)
      .then((rows) => {
        this.rows(rows);
        this.loading(false);
      })
      .catch(() => {
        this.errorMessage(i18n.t('Failed to load observations.')());
        this.loading(false);
      });
  }

  dimensionsLabel = (row: dashboardApi.GroupObservationRow): string =>
    row.dimensions.map((dimension) => `${dimension.dimension_meta_name}: ${dimension.name}`).join(', ');

  visitLabel = (row: dashboardApi.GroupObservationRow): string => {
    if (row.visit_name && row.visit_date) {
      return `${row.visit_name} (${row.visit_date})`;
    }
    return row.visit_name || row.visit_date || '';
  };

  deleteRow = async (row: dashboardApi.GroupObservationRow) => {
    // Derived (computed) values have no backing fact and cannot be deleted directly.
    if (row.is_derived || !row.fact_id) {
      return;
    }
    try {
      await confirmDialog(i18n.t('Delete value')(), [
        i18n.t('Are you sure you want to delete this value? This action cannot be undone.')(),
      ]);
    } catch {
      return; // cancelled
    }
    try {
      await factsApi.removeObservation(row.fact_id, {
        measurement_meta_id: String(row.measurement_meta_id),
      });
      this.rows.remove(row);
      this.params.onChanged();
    } catch {
      this.errorMessage(i18n.t('Failed to delete the value.')());
    }
  };

  close = () => {
    this.params.result.resolve();
  };
}

ko.components.register('observations-dialog', {
  viewModel: ObservationsDialog,
  template,
});

export function openObservationsDialog(args: ObservationsDialogArgs): Promise<void> {
  return app.formsStackController.push({
    title: i18n.t('Observations')(),
    name: 'observations-dialog',
    params: { ...args, result: new Deferred<void>() },
    isBig: true,
    showNav: false,
  });
}
